import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, lastValueFrom, map } from 'rxjs';
import { FeatureFlagsLexiClient, UpdateFeatureFlagRequest } from '@lexi-clients/lexi';

export enum FeatureFlags {
  PREVIEW = 'Preview',
  COMPTABILITE_AMELIORATIONS = 'ComptabiliteAmeliorations',
  SAISIE_DEVIS = 'SaisieDevis',
}

interface FeatureFlagState {
  [key: string]: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private readonly featureFlagState$ = new BehaviorSubject<FeatureFlagState>({});
  private isLoaded = false;

  constructor(private readonly featureFlagsLexiClient: FeatureFlagsLexiClient) {
    this.loadFeatureFlags();
  }

  private async loadFeatureFlags(): Promise<void> {
    try {
      // Migrer depuis localStorage si nécessaire
      this.migrateFromLocalStorage();

      // Charger depuis l'API
      const flags: FeatureFlagState = await lastValueFrom(this.featureFlagsLexiClient.getAllFeatureFlags());
      // On le fait avant de passer par le BehaviorSubject pour que lorsqu'on utilise isFeatureEnabled
      // isLoaded soit déjà à true
      this.isLoaded = true;
      this.featureFlagState$.next(flags);
    } catch (error) {
      console.warn('Erreur lors du chargement des feature flags, utilisation des valeurs par défaut:', error);
      this.isLoaded = true;
      // Valeurs par défaut en cas d'erreur
      this.featureFlagState$.next({
        [FeatureFlags.PREVIEW]: false,
        [FeatureFlags.COMPTABILITE_AMELIORATIONS]: false,
        [FeatureFlags.SAISIE_DEVIS]: false,
      });
    }
  }

  private migrateFromLocalStorage(): void {
    const storedState = localStorage.getItem('featureFlags');
    if (!storedState) return;

    try {
      const parsed = JSON.parse(storedState);
      let shouldMigrate = false;

      // Migration depuis l'ancien format array
      if (Array.isArray(parsed)) {
        const hasFeatures = parsed.some(flag =>
          flag === 'Voir tous les bons des sites permis' ||
          flag === 'Importer des lignes de bons via le csv'
        );

        if (hasFeatures) {
          this.updateFeatureFlagInBackend(FeatureFlags.PREVIEW, true);
          shouldMigrate = true;
        }
      }
      // Migration depuis l'ancien format objet (OptimizedBonFrontend -> Preview)
      else if (parsed[FeatureFlags.PREVIEW] === true || parsed['OptimizedBonFrontend'] === true) {
        this.updateFeatureFlagInBackend(FeatureFlags.PREVIEW, true);
        shouldMigrate = true;
      }

      if (shouldMigrate) {
        // Supprimer l'ancien localStorage après migration
        localStorage.removeItem('featureFlags');
        console.log('Migration des feature flags vers la base de données effectuée');
      }
    } catch (error) {
      console.warn('Erreur lors de la migration des feature flags:', error);
      localStorage.removeItem('featureFlags');
    }
  }

  getFeatureFlags(): FeatureFlags[] {
    return Object.values(FeatureFlags);
  }

  isPreviewEnabled(): boolean {
    return this.isFeatureEnabled(FeatureFlags.PREVIEW);
  }

  isComptabiliteAmeliorationsEnabled(): boolean {
    return this.isFeatureEnabled(FeatureFlags.COMPTABILITE_AMELIORATIONS);
  }

  async updateFeatureFlag(feature: FeatureFlags, isEnabled: boolean): Promise<void> {
    try {
      await this.updateFeatureFlagInBackend(feature, isEnabled);

      // Mettre à jour l'état local
      const currentState = this.featureFlagState$.value;
      this.featureFlagState$.next({
        ...currentState,
        [feature]: isEnabled
      });

      // Recharger la page pour appliquer les changements
      location.reload();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du feature flag:', error);
      throw error;
    }
  }

  private async updateFeatureFlagInBackend(feature: FeatureFlags, isEnabled: boolean): Promise<void> {
    const request: UpdateFeatureFlagRequest = { isEnabled };
    await lastValueFrom(this.featureFlagsLexiClient.updateFeatureFlagStatus(feature, request));
  }

  isFeatureEnabled(feature: FeatureFlags): boolean {
    if (!this.isLoaded) {
      // Retourner false si pas encore chargé
      return false;
    }
    return this.featureFlagState$.value[feature] || false;
  }

  /**
   * Observable pour écouter les changements d'état des feature flags
   */
  getFeatureFlagsState(): Observable<FeatureFlagState> {
    return this.featureFlagState$.asObservable();
  }

  isFeatureEnabled$(feature: FeatureFlags): Observable<boolean> {
    return this.featureFlagState$.asObservable().pipe(
      map((state) => state[feature] || false)
    );
  }

  /**
   * Méthode async pour s'assurer que les feature flags sont chargés
   */
  async waitForLoad(): Promise<void> {
    if (this.isLoaded) return;

    // Attendre que les feature flags soient chargés
    await new Promise<void>((resolve) => {
      const subscription = this.featureFlagState$.subscribe(() => {
        if (this.isLoaded) {
          subscription.unsubscribe();
          resolve();
        }
      });
    });
  }
}
